/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Switch from '@ion/dls/src/components/switch/Switch';
import ChevronForwardIcon from '@ion/dls/src/icons/current/ChevronForwardIcon';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {HistoryInputEvents, HistoryReportTypeEnum, LabelContentType} from '@ion/dls/src/types/types';
import {LabelContentClient} from '../markers/AssetMarkers';

// defines the prop definitions shared between all the LayerSettings
export type LayerSettingsProps = {
  map: __esri.Map;
  isEnabled: boolean;
  isClusteringActive?: boolean;
  isLargeFleetMode?: boolean;
  onToggle: Function;
  onToggleExpand?: Function;
  onToggleClustering?: Function;
  onToggleLabelling?: Function;
  expandedLayer?: string | null;
  assetsHistoryLoading?: boolean;
  isHideNonSelectedAssetsActive?: boolean;
  loadAssetHistory?: Function;
  setAssetsHistoryLive?: Function;
  setHistoryInterval?: Function;
  historyIntervalInHours?: number;
  historyReportType?: HistoryReportTypeEnum;
  historyInputEvents?: HistoryInputEvents[];
  setHistoryReportType?: Function;
  setHistoryInputEvents?: Function;
  setFleetLayerPersistenceActive?: Function;
  layers?: string[];
  labelContent: LabelContentType
} & LabelContentClient;

// defines the prop definitions shared between all the LayerSettings SubLayers
export type LayerSettingsSubLayersProps = {
  changeOpacity: Function,
  opacity: number,
  subLayersChip?: Array<{label: string, name: string, isSelected: Boolean}>,
  updateSubLayersChip?: Function,
  title?: string
};

// defines the opacity range shared between all the LayerSettings
export const LayerSettingsMarks = [{
  value : 0,
  label : '0'
}, {
  value : 0.5,
  label : '50'
}, {
  value : 1,
  label : '100'
}];

// defines the styles shared between all the LayerSettings
export const LayerSettingsStyles = (): Record<string, {}> => {
  return {
    root : {
      flexGrow : 1,
      fontSize : '10px !important'
    },
    square : {
      height : '15px',
      width  : '15px',
      margin : '10px'
    },
    button : {
      color : `${dlsColor('grey3')} !important`,
      ...dlsAlt('maps', 'layer', 'tabHeader')
    },
    buttonSelected : {
      color : `${dlsColor('aaBlue')} !important`,
      ...dlsAlt('maps', 'layer', 'tabHeader')
    },
    paper : {
      border    : 'none !important',
      boxShadow : 'none !important'
    },
    layers : {
      display       : 'flex',
      paddingBottom : '10px'
    },
    subLayer : {
      flexGrow : '0.2'
    },
    subLayerRight : {
      flex : 1
    },
    trafficContainer : {
      display : 'flex'
    },
    trafficItemLeft : {
      flexGrow : '0.3'
    },
    weatherContainer : {
      display : 'block'
    },
    assetContainer : {
      paddingTop    : '10px',
      paddingBottom : '10px',
      display       : 'flex',
      flexWrap      : 'wrap',
      border        : `2px solid ${dlsColor('grey5')}`,
      transition    : 'all 1s linear 0s',
      paddingLeft   : '10px',
      marginTop     : '13px'
    },
    legendLabel : {
      textAlign : 'left',
      ...dlsAlt('maps', 'layer', 'chipHeader', 'label')
    },
    legendIcon : {
      width          : '100%',
      display        : 'flex',
      justifyContent : 'flex-start',
      alignItems     : 'center'
    },
    circle : {
      width        : '16px',
      height       : '16px',
      borderRadius : '50%',
      margin       : '10px 5px 10px 10px'
    },
    borderCircle1 : {
      width        : '12px',
      height       : '12px',
      borderRadius : '50%',
      border       : '2px solid',
      margin       : '10px 5px 10px 10px'
    },
    borderCircle2 : {
      width        : '8px',
      height       : '8px',
      borderRadius : '50%',
      border       : '4px solid',
      margin       : '10px 5px 10px 10px'
    },
    rectangle : {
      width  : '16px',
      height : '4px',
      margin : '16px 5px 16px 15px'
    },
    subLabel : {
      ...dlsAlt('maps', 'layer', 'chipHeader', 'label')
    },
    chip : {
      ...dlsAlt('maps', 'layer', 'chipHeader')
    },
    accordionDetails : {
      display : 'block'
    },
    // overrides AccordionSummary's own MUI default minHeight (48px) so the
    // current-theme Map Layers panel's closed row height matches the
    // Figma spec (44px) exactly, rather than being forced taller by the
    // accordion header's own built-in minimum ...
    currentSummaryRoot : {
      minHeight : '44px !important'
    }
  };
};

/**
 * Provides the Accordion props every LayerSettings row shares: in the
 * current theme, a chevron expand icon that rotates down when open, no
 * expanded-state border, and a 44px summary height; in the legacy theme,
 * the Accordion's own defaults.
 *
 * ```typescript
 *
 *   import {getLayerSettingsAccordionProps} from './LayerSettings';
 *   ...
 *
 *   <Accordion {...getLayerSettingsAccordionProps(isCurrent, classes)}
 *     id={label}
 *     header={header}
 *     details={details} />
 *
 * ```
 *
 * @param   isCurrent Whether the current theme is active.
 * @param   classes   The row's classes, from `LayerSettingsStyles()`.
 * @returns           The theme-specific Accordion props.
 */
export const getLayerSettingsAccordionProps = (isCurrent: boolean, classes: Record<string, string>) => ({
  expandIcon            : isCurrent ? <ChevronForwardIcon /> : undefined,
  expandIconRotatesDown : isCurrent,
  disableExpandedBorder : isCurrent,
  summaryRootClass      : isCurrent ? classes.currentSummaryRoot : undefined
});

/**
 * Provides the current theme Map Layers panel's row wrapper style, shared
 * by the fixed layer rows (LayerChooser.tsx) and the operational layer rows
 * (OperationalLayersSettings.tsx), so every row keeps the same full-width
 * layout and divider.
 *
 * ```typescript
 *
 *   import {LayerSettingsCurrentRowStyles} from './LayerSettings';
 *   ...
 *
 *   const useStyles = makeStyles(() => ({
 *     ...LayerSettingsCurrentRowStyles()
 *   }));
 *
 * ```
 *
 * @returns The `currentRow` style rule.
 */
export const LayerSettingsCurrentRowStyles = (): Record<string, {}> => ({
  currentRow : {
    flexShrink   : 0,
    borderBottom : `1px solid ${dlsColor('grey5')}`,
    '& > *'      : {
      width : '100%'
    }
  }
});

//defines the prop definitions for the LayerSettingsHeader
export type LayerSettingsHeaderProps = {
  label: string,
  onToggle: Function,
  isSelected?: boolean,
}

// defines the styles used by the LayerSettingsHeader
const useStyles = makeStyles(theme => ({
  root : {
    width          : '100%',
    display        : 'flex',
    justifyContent : 'flex-start',
    alignItems     : 'center',
    marginLeft     : '-16px'
  },
  currentRoot : {
    width      : '100%',
    display    : 'flex',
    alignItems : 'center',
    gap        : '8px'
  },
  currentLabel : {
    flex       : 1,
    fontFamily : 'Inter',
    fontSize   : '14px',
    fontWeight : 400,
    lineHeight : '20px',
    color      : dlsColor('grey1')
  }
}));

/**
 * @LayerSettingsHeader
 *
 * This component defines the header which is shared between all the LayerSettings.
 *
 * ```typescript
 *
 *   <LayerSettingsHeader label={}
 *     isSelected={}
 *     onToggle={} />
 *
 * ```
 */
export const LayerSettingsHeader = ({
  label,
  isSelected,
  onToggle
}: LayerSettingsHeaderProps) => {
  const classes: any = useStyles();
  const {isCurrent}  = useThemeContext();

  return isCurrent ? (
    <div className={classes.currentRoot}>
      <Switch checked={isSelected} onChange={onToggle} />
      <span className={classes.currentLabel}>
        {label}
      </span>
    </div>
  ) : (
    <div className={classes.root}>
      <Switch checked={isSelected}
        label={label}
        onChange={onToggle}/>
    </div>);
};
